import { useState, useEffect, useRef } from 'react';
import { sanitizeRedirectUrl } from '@common/utils/sanitizeRedirectUrl';
import { Button } from '../shadcn/button';
import useTheme from '../../hooks/useTheme';

// Store cancelled URL outside component to persist across re-renders
let cancelledUrl: string | null = null;

interface TranslationStrings {
  redirectingIn: string;
  seconds: string;
  cancel: string;
  copied: string;
  cantClickButton: string;
}

interface CompletionButtonProps {
  url: string;
  text: string;
  fallbackText?: string;
  autoRedirect?: boolean;
  translations?: TranslationStrings;
  primaryColor?: string;
  chatPrimary?: string;
  chatSecondary?: string;
}

/**
 * Default English translations for the completion button
 */
const DEFAULT_TRANSLATIONS: TranslationStrings = {
  redirectingIn: 'Redirecting in',
  seconds: 'seconds...',
  cancel: 'Cancel',
  copied: 'Copied!',
  cantClickButton: "Can't click the button? Copy this link:",
};

/**
 * CompletionButton component displays a completion link with optional auto-redirect
 * Supports localized countdown timer and cancel functionality
 */
function CompletionButton({
  url,
  text,
  fallbackText,
  autoRedirect = false,
  translations = DEFAULT_TRANSLATIONS,
  primaryColor,
  chatPrimary,
  chatSecondary,
}: CompletionButtonProps) {
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(() => {
    // Initialize only once, not on every render
    return autoRedirect && cancelledUrl !== url ? 10 : null;
  });
  const { currentTheme } = useTheme();
  // Guards against the auto-redirect timer and a manual click both firing a
  // navigation to the same (single-use) panel link when the click happens
  // close to when the countdown would reach 0.
  const hasNavigatedRef = useRef(false);

  // Use provided primary color, theme primary color, or fallback
  const themeColor = primaryColor || currentTheme?.primary || '#4CAF50';

  useEffect(() => {
    if (countdown !== null && countdown > 0 && cancelledUrl !== url) {
      const timer = setTimeout(() => {
        setCountdown((prev) => (prev !== null ? prev - 1 : prev));
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (countdown === 0 && cancelledUrl !== url && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      // TELV1-551 — defense in depth: the parser already blocks script
      // schemes, but this is the navigation sink, so it guards too.
      const safeUrl = sanitizeRedirectUrl(url);
      if (safeUrl) window.location.href = safeUrl;
    }
    return undefined;
  }, [countdown, url]);

  const handleClick = () => {
    // Clear countdown and navigate
    setCountdown(null);
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    const safeUrl = sanitizeRedirectUrl(url);
    if (safeUrl) window.location.href = safeUrl;
  };

  const handleCancel = () => {
    // Set cancelled URL to persist across re-renders
    cancelledUrl = url;
    setCountdown(null);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="mt-4 space-y-3">
      {countdown !== null && cancelledUrl !== url && (
        <div
          className="mb-3 p-3 rounded-lg border"
          style={{
            backgroundColor: `${themeColor}15`, // 15 for opacity
            borderColor: `${themeColor}40`, // 40 for opacity
          }}
        >
          <div className="flex items-center justify-between">
            <p className="text-sm" style={{ color: themeColor }}>
              {translations.redirectingIn} <span className="font-bold">{countdown || 0}</span>{' '}
              {translations.seconds}
            </p>
            <button
              onClick={handleCancel}
              className="text-sm underline"
              style={{ color: themeColor }}
              type="button"
            >
              {translations.cancel}
            </button>
          </div>
          <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-1000"
              style={{
                backgroundColor: themeColor,
                width: `${countdown !== null ? ((10 - countdown) / 10) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}
      <Button
        onClick={handleClick}
        className="w-full py-7 px-6 text-white font-semibold rounded-lg transition-all duration-200 text-lg shadow-lg hover:brightness-90"
        style={{
          backgroundColor:
            chatPrimary && chatSecondary && chatPrimary !== chatSecondary
              ? chatPrimary
              : primaryColor || currentTheme?.primary || '#4CAF50',
        }}
        type="button"
        size="lg"
      >
        {text}
      </Button>
      <div className="text-sm text-gray-500 text-center">
        <p>{fallbackText || translations.cantClickButton}</p>
        <div className="relative mt-1">
          <code className="block p-2 pr-12 bg-gray-100 rounded text-xs break-all select-all">
            {url}
          </code>
          <button
            onClick={handleCopy}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-gray-600 hover:text-gray-800 transition-colors rounded hover:bg-gray-200"
            type="button"
            aria-label="Copy URL"
          >
            {copied ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                />
              </svg>
            )}
          </button>
          {copied && (
            <span className="absolute -top-8 right-0 bg-gray-800 text-white text-xs px-2 py-1 rounded">
              {translations.copied}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompletionButton;
